/**
 * Strapi API client and utilities
 */

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1338';

/**
 * Get the full URL for a Strapi API endpoint
 */
export function getStrapiUrl(path: string = ''): string {
  return `${STRAPI_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Get the full URL for a Strapi media file
 */
export function getStrapiMediaUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return getStrapiUrl(url);
}

/**
 * Check if we're in preview mode
 */
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem('preview-mode') === 'true';
}

/**
 * Get preview document ID from session storage
 */
export function getPreviewDocumentId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('preview-documentId');
}

/**
 * Fetch data from Strapi API
 * Automatically includes draft/preview params when in preview mode
 */
export async function fetchStrapi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  let url = getStrapiUrl(`/api${endpoint}`);

  // Add preview parameters if in preview mode
  if (isPreviewMode()) {
    const urlObj = new URL(url);
    urlObj.searchParams.set('status', 'draft');
    urlObj.searchParams.set('publicationState', 'preview');
    url = urlObj.toString();
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Strapi API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Strapi API response wrapper type
 */
export interface StrapiResponse<T> {
  data: T;
  meta?: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

/**
 * Strapi single type response
 */
export interface StrapiSingleResponse<T> {
  data: {
    id: number;
    attributes: T;
  };
  meta?: Record<string, unknown>;
}


